import { useState, useEffect, useRef } from 'react'
import api from '../api/client'
import Boton from './Boton'
import { useAuth } from '../context/AuthContext'
import { fmtFechaHora } from '../utils/fecha'

/**
 * Hilo de mensajes libres de una nota de venta ("está esperando un vino del
 * proveedor"). Cualquier usuario, en cualquier estado — también mientras se
 * edita el borrador. Solo se agrega: el hilo es registro, no se edita ni borra.
 *
 * Distinto de los Avisos (dirigidos a una persona, con acuse): esto es la
 * cartelera del pedido.
 */

// Color estable por persona (hash del nombre → paleta suave del sistema).
const PALETA = [
  { bg: '#eff6ff', color: '#1d4ed8' },  // azul
  { bg: '#f5f3ff', color: '#6d28d9' },  // violeta
  { bg: '#ecfdf5', color: '#047857' },  // verde
  { bg: '#fff7ed', color: '#c2410c' },  // naranja
  { bg: '#fdf2f8', color: '#be185d' },  // rosa
  { bg: '#f0fdfa', color: '#0f766e' },  // teal
]

function colorDe(nombre) {
  let h = 0
  for (const c of nombre || '') h = (h * 31 + c.charCodeAt(0)) % 997
  return PALETA[h % PALETA.length]
}

function iniciales(nombre) {
  const partes = (nombre || '').trim().split(/\s+/)
  return ((partes[0]?.[0] || '?') + (partes[1]?.[0] || '')).toUpperCase()
}

export default function MensajesNVCard({ nvId }) {
  const { user } = useAuth()
  const [mensajes, setMensajes] = useState([])
  const [texto, setTexto] = useState('')
  const [enviando, setEnviando] = useState(false)
  const listaRef = useRef(null)

  useEffect(() => {
    if (!nvId) return
    let vivo = true
    api.get(`/notas-venta/${nvId}/mensajes`)
      .then(({ data }) => { if (vivo) setMensajes(data) })
      .catch(() => {})
    return () => { vivo = false }
  }, [nvId])

  // El hilo se lee de arriba hacia abajo: al entrar (o al agregar) queda
  // mostrando lo último.
  useEffect(() => {
    const el = listaRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [mensajes.length])

  const enviar = async () => {
    const t = texto.trim()
    if (!t || enviando) return
    setEnviando(true)
    try {
      const { data } = await api.post(`/notas-venta/${nvId}/mensajes`, { texto: t })
      setMensajes(prev => [...prev, data])
      setTexto('')
    } catch (e) {
      alert(e.response?.data?.message || 'No se pudo agregar el mensaje.')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div style={st.card}>
      <div style={st.header}>
        <h3 style={st.title}>💬 Mensajes del pedido</h3>
        {mensajes.length > 0 && <span style={st.count}>{mensajes.length}</span>}
      </div>

      {mensajes.length === 0 ? (
        <p style={st.empty}>
          Todavía no hay mensajes. Dejá acá cualquier aclaración para el resto
          del equipo — ej: <em>"esperando un vino del proveedor"</em>.
        </p>
      ) : (
        <div ref={listaRef} style={st.lista}>
          {mensajes.map(m => {
            const esMio = user?.nombre && m.usuario === user.nombre
            const col = colorDe(m.usuario)
            return (
              <div key={m.id} style={st.fila}>
                <span style={{ ...st.avatar, background: col.bg, color: col.color }}>
                  {iniciales(m.usuario)}
                </span>
                <div style={{ ...st.burbuja, ...(esMio ? st.burbujaMia : {}) }}>
                  <div style={st.meta}>
                    <strong style={{ color: '#1e293b' }}>{esMio ? 'Vos' : (m.usuario || 'Sistema')}</strong>
                    <span style={st.hora}>{fmtFechaHora(m.fecha)}</span>
                  </div>
                  <div style={st.texto}>{m.texto}</div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div style={st.inputRow}>
        <textarea
          value={texto}
          onChange={e => setTexto(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar() } }}
          placeholder="Escribí un mensaje para el equipo…"
          rows={2}
          // Es prosa de chat, no un campo de formulario: sin el data-nocaps la
          // regla global de MAYÚSCULAS lo mostraba gritando mientras se tipea.
          data-nocaps
          style={st.textarea}
        />
        <div style={st.inputAcciones}>
          {/* send, no primary: la primary de la pantalla es "Enviar paquete" /
              "Guardar" — una sola por pantalla (mapa de Boton.jsx). */}
          <Boton variante="send" sm onClick={enviar} disabled={enviando || !texto.trim()}>
            {enviando ? 'Enviando…' : '→ Enviar'}
          </Boton>
          <span style={st.hint}>Enter envía · Shift+Enter, renglón</span>
        </div>
      </div>
    </div>
  )
}

const st = {
  card: { background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: '1rem' },
  header: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.9rem' },
  // Título de card del sistema (11px uppercase slate) — igual que el resto de
  // las cards del detalle de NV y que las pantallas de detalle de referencia.
  title: { margin: 0, fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' },
  count: { background: '#eff6ff', color: '#1d4ed8', fontSize: '0.72rem', fontWeight: 700, padding: '1px 8px', borderRadius: 999, fontVariantNumeric: 'tabular-nums' },
  empty: { color: '#94a3b8', fontSize: '0.8125rem', margin: '0 0 0.25rem', lineHeight: 1.5 },

  lista: { display: 'flex', flexDirection: 'column', gap: '0.625rem', maxHeight: 340, overflowY: 'auto', paddingRight: 2 },
  fila: { display: 'flex', gap: 8, alignItems: 'flex-start' },
  avatar: {
    width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.66rem', fontWeight: 700, marginTop: 2,
  },
  burbuja: { flex: 1, minWidth: 0, background: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: '8px', padding: '0.5rem 0.7rem' },
  burbujaMia: { background: '#eff6ff', border: '1px solid #dbeafe' },
  meta: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.75rem', fontSize: '0.75rem' },
  hora: { color: '#94a3b8', fontSize: '0.68rem', fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', whiteSpace: 'nowrap' },
  texto: { marginTop: 3, fontSize: '0.8125rem', color: '#1e293b', whiteSpace: 'pre-wrap', overflowWrap: 'break-word', lineHeight: 1.45 },

  inputRow: { marginTop: '0.75rem' },
  textarea: {
    width: '100%', boxSizing: 'border-box', padding: '0.55rem 0.75rem',
    border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.8125rem',
    fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.4,
  },
  inputAcciones: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 6 },
  hint: { fontSize: '0.68rem', color: '#cbd5e1' },
}
